import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { useContext, useEffect } from 'react';
import { AdContext } from './AdContext';
import { AdProviders, ADUNIT_PROVIDER_SAFETY_KEY, FREESTAR_PREFIX } from './constants';
import { isSSR } from './utils';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
export function useAdUnit(provider, unitId, safety, safetyState, options) {
    var _adsPxl, _adsHmn, _adsTargeting;
    const { DaAds } = useContext(AdContext);
    const { adsPxl, adsHmn, adsTargeting } = useContext(SessionContext);
    const isPxl = useFlag('ads_pxl');
    const isHmn = useFlag('ads_hmn');
    let className = '';
    // TODO if freestar is perma give them their own ID set
    if (provider === AdProviders.Freestar) {
        unitId = `${FREESTAR_PREFIX}${unitId}`;
    }
    // Hashtag use classnames to target and config units
    if (provider === AdProviders.Hashtag) {
        className = `htlad-${unitId}`;
    }
    // Pixelate / Human - whitelisted units
    let pixelateOn = ((_adsPxl = adsPxl) === null || _adsPxl === void 0 ? void 0 : _adsPxl.enabled) ? 'on' : 'off';
    let humanOn = ((_adsHmn = adsHmn) === null || _adsHmn === void 0 ? void 0 : _adsHmn.enabled) ? 'on' : 'off';
    const unitMatches = [
        /anchor/
    ];
    unitMatches.forEach(function(m) {
        if (!m.test(unitId)) {
            pixelateOn = 'off';
            humanOn = 'off';
        }
    });
    // Pixelate / Human - off on dev
    if (process.env.NODE_ENV !== 'production' || process.env.XHR_ENV === 'vm') {
        pixelateOn = 'off';
        humanOn = 'off';
    }
    let pxl = {};
    if (isPxl) {
        var _adsPxl1;
        var _adsPxl_sample;
        pxl = {
            ivtdebug: 'on',
            pxon: pixelateOn,
            pxsample: (_adsPxl_sample = (_adsPxl1 = adsPxl) === null || _adsPxl1 === void 0 ? void 0 : _adsPxl1.sample) !== null && _adsPxl_sample !== void 0 ? _adsPxl_sample : '100000000'
        };
    }
    let hmn = {};
    if (isHmn) {
        var _adsHmn1;
        var _adsHmn_sample;
        hmn = {
            ivtdebug: 'on',
            hmnon: humanOn,
            hmnsample: (_adsHmn_sample = (_adsHmn1 = adsHmn) === null || _adsHmn1 === void 0 ? void 0 : _adsHmn1.sample) !== null && _adsHmn_sample !== void 0 ? _adsHmn_sample : '100000000'
        };
    }
    var _options_targeting;
    // Add default targeting data, allow consumer to override
    options.targeting = _object_spread({}, toSlotTargeting(unitId, (_adsTargeting = adsTargeting) === null || _adsTargeting === void 0 ? void 0 : _adsTargeting.slot), pxl, hmn, (_options_targeting = options.targeting) !== null && _options_targeting !== void 0 ? _options_targeting : {});
    // Add safety to the options
    options[ADUNIT_PROVIDER_SAFETY_KEY[provider]] = safety;
    // In ssr we register on render
    if (isSSR()) {
        var _DaAds_registerUnit, _this;
        (_this = DaAds) === null || _this === void 0 ? void 0 : (_DaAds_registerUnit = _this.registerUnit) === null || _DaAds_registerUnit === void 0 ? void 0 : _DaAds_registerUnit.call(_this, provider, unitId, safety, options);
    }
    const biContext = useContext(BiContext);
    const publicSession = useContext(SessionContext);
    // On client we have side effect + kill on unmount
    useEffect(()=>{
        if (!isSSR() && safetyState === 'ready') {
            var _DaAds_registerUnit, _this;
            (_this = DaAds) === null || _this === void 0 ? void 0 : (_DaAds_registerUnit = _this.registerUnit) === null || _DaAds_registerUnit === void 0 ? void 0 : _DaAds_registerUnit.call(_this, provider, unitId, safety, options);
        }
        return ()=>{
            var _DaAds_killUnit, _this;
            (_this = DaAds) === null || _this === void 0 ? void 0 : (_DaAds_killUnit = _this.killUnit) === null || _DaAds_killUnit === void 0 ? void 0 : _DaAds_killUnit.call(_this, unitId, safety);
        };
    // only run on safety change, DaAds will handle recreation
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        safety,
        safetyState
    ]);
    // sync bi data
    useEffect(()=>{
        if (!isSSR()) {
            var _DaAds_setBiContext, _this;
            (_this = DaAds) === null || _this === void 0 ? void 0 : (_DaAds_setBiContext = _this.setBiContext) === null || _DaAds_setBiContext === void 0 ? void 0 : _DaAds_setBiContext.call(_this, biContext, publicSession);
        }
    }, [
        DaAds,
        biContext,
        publicSession
    ]);
    return {
        // suppress + danger is hack that forces current react (16+)
        // to NOT clear the inner element on hydration, this stops
        // the clobbering of the adunit on first client render.
        suppressHydrationWarning: true,
        dangerouslySetInnerHTML: {
            __html: ''
        },
        id: unitId,
        'data-canfs': 'yes',
        'data-safety': safety,
        advertIsOff: safety === 'off' ? 'off' : false,
        className
    };
}
/**
 * Takes the backend struct and converts it into taregting object
 */ function toSlotTargeting(unitId, slotTargeting) {
    const targeting = {};
    const filtered = slotTargeting.filter((t)=>t.unitId === unitId);
    if (filtered.length > 0) {
        for (const targetingKV of filtered){
            targeting[targetingKV.key] = targetingKV.value;
        }
    }
    return targeting;
}
